import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Fingerprint } from 'lucide-react';
import { getSecurityType, SECURITY_TYPE, getColorTheme, getActiveThemeColorSet, getPasscode, setPasscodeEnabled } from '@/utils/storage';
import { authenticateWithBiometrics, isBiometricsAvailable } from '@/utils/biometricAuth';
import { RebuildBlockLogo } from '@/components/RebuildBlockLogo';

export const Passcode = ({ onUnlock }) => {
  const initialTheme = getActiveThemeColorSet();

  const [error, setError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [biometricsAvailable, setBiometricsAvailable] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];
  const passcodeRef = useRef(['', '', '', '']);
  const hasAutoPromptedRef = useRef(false);
  const [, forceUpdate] = useState({});
  
  const correctPasscode = getPasscode();
  const securityType = getSecurityType();
  const isPasscodeMode = securityType === SECURITY_TYPE.PASSCODE;

  useEffect(() => {
    setCurrentTheme(getActiveThemeColorSet());
    
    const checkBiometrics = async () => {
      const available = await isBiometricsAvailable();
      setBiometricsAvailable(available);
    };
    checkBiometrics();
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;

    root.style.setProperty('--theme-primary', currentTheme.primary);
    root.style.setProperty('--theme-secondary', currentTheme.secondary);
    root.style.setProperty('--theme-accent', currentTheme.accent);
    root.style.setProperty('--theme-light', currentTheme.light);
    root.style.setProperty('--theme-btn-bg', currentTheme.primary);
    root.style.setProperty('--theme-btn-hover', currentTheme.secondary);
    root.style.setProperty('--theme-border', currentTheme.secondary);
    root.style.setProperty('--theme-input-bg', `${currentTheme.light}80`);
    root.style.setProperty('--theme-text-primary', currentTheme.textPrimary || '#2D2D35');
    root.style.setProperty('--theme-text-secondary', currentTheme.textSecondary || '#73737D');
    root.style.setProperty('--theme-logo-text', currentTheme.logoText || '#F9F9FF');
    document.body.setAttribute('data-theme', getColorTheme());
  }, [currentTheme]);

  useEffect(() => {
    const root = document.getElementById('root');
    const previousBodyBg = document.body.style.backgroundColor;
    const previousRootBg = root?.style.backgroundColor;
    const previousBodyColorScheme = document.body.style.colorScheme;
    const previousRootColorScheme = document.documentElement.style.colorScheme;

    document.body.style.backgroundColor = currentTheme.light;
    if (root) root.style.backgroundColor = currentTheme.light;
    document.body.style.colorScheme = 'light';
    document.documentElement.style.colorScheme = 'light';

    return () => {
      document.body.style.backgroundColor = previousBodyBg;
      if (root) root.style.backgroundColor = previousRootBg || '';
      document.body.style.colorScheme = previousBodyColorScheme;
      document.documentElement.style.colorScheme = previousRootColorScheme;
    };
  }, [currentTheme.light]);

  useEffect(() => {
  const handleGlobalKeyPress = (e) => {
    console.log('🌍 GLOBAL KEY PRESS:', e.key);
  };
  window.addEventListener('keypress', handleGlobalKeyPress);
  return () => window.removeEventListener('keypress', handleGlobalKeyPress);
}, []);

  const handleFaceIDUnlock = useCallback(async () => {
    if (isAuthenticating) return;
    setIsAuthenticating(true);
    setError('');
    
    const result = await authenticateWithBiometrics('Unlock RE-BUILD-ME-PRO with Face ID');
    
    if (result.success) {
      setIsAuthenticating(false);
      onUnlock();
    } else {
      setIsAuthenticating(false);
      if (result.error === 'cancelled') {
        setError('Face ID cancelled.');
      } else if (result.fallbackToPasscode) {
        setError('Face ID unavailable. Use your iPhone passcode when prompted.');
      } else {
        setError('Face ID failed.');
      }
      setTimeout(() => setError(''), 3000);
    }
  }, [isAuthenticating, onUnlock]);

  useEffect(() => {
    if (isPasscodeMode) return;
    if (!biometricsAvailable) return;
    if (hasAutoPromptedRef.current) return;

    hasAutoPromptedRef.current = true;
    void handleFaceIDUnlock();
  }, [isPasscodeMode, biometricsAvailable, handleFaceIDUnlock]);

  const updatePasscode = (index, value) => {
    // Only take the last character if multiple
      console.log('🔴 UPDATE CALLED:', { index, value, timestamp: Date.now() });

    let newValue = value;
    if (newValue && newValue.length > 1) {
      newValue = newValue.charAt(newValue.length - 1);
    }
    
    // Only allow numbers
    if (newValue && !/^\d$/.test(newValue)) return;
    
    // Update ref (doesn't trigger re-render)
    passcodeRef.current[index] = newValue || '';
    
    // Force re-render to show the digit
    forceUpdate({});
    
    // Auto-focus next input
    if (newValue && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
    
    // Check if all digits are filled
    const allFilled = passcodeRef.current.every(digit => digit && digit.length === 1);
    if (allFilled) {
      const enteredCode = passcodeRef.current.join('');
      if (enteredCode === correctPasscode) {
        setPasscodeEnabled(true);
        onUnlock();
      } else {
        setError('Incorrect passcode');
        // Clear all
        passcodeRef.current = ['', '', '', ''];
        forceUpdate({});
        inputRefs[0].current?.focus();
        setTimeout(() => setError(''), 2000);
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (passcodeRef.current[index]) {
        // Clear current box
        passcodeRef.current[index] = '';
        forceUpdate({});
      } else if (index > 0) {
        // Move to previous box and clear it
        passcodeRef.current[index - 1] = '';
        forceUpdate({});
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const handleClearAll = () => {
    passcodeRef.current = ['', '', '', ''];
    forceUpdate({});
    inputRefs[0].current?.focus();
    setError('');
  };

  if (isPasscodeMode) {
    return (
      <div
        className="fixed inset-0 px-6 overflow-hidden"
        style={{
          height: '100dvh',
          boxSizing: 'border-box',
          backgroundColor: currentTheme.light,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="w-full h-full flex flex-col">
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full px-2">
              <RebuildBlockLogo className="w-full h-auto" scaleText={0.9} />
            </div>
          </div>

          <div className="max-w-sm mx-auto w-full pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))]">
            <h1 className="type-page-title text-center mb-4" style={{ color: currentTheme.primary }}>
              Enter Passcode
            </h1>

            <div className="flex justify-center gap-4 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  ref={inputRefs[i]}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={passcodeRef.current[i]}
                  onChange={(e) => updatePasscode(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  onFocus={(e) => e.target.select()}
                  className="w-16 h-16 text-center text-2xl rounded-xl border-2 focus:outline-none"
                  style={{
                    borderColor: currentTheme.secondary,
                    backgroundColor: currentTheme.light,
                    color: currentTheme.primary
                  }}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-testid={`passcode-input-${i}`}
                />
              ))}
            </div>

            {error && (
              <p className="text-red-500 text-center mb-3">
                {error}
              </p>
            )}

            <button
              onClick={handleClearAll}
              className="w-full text-center text-sm opacity-50"
              style={{ color: currentTheme.secondary }}
            >
              Clear all
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 px-6 overflow-hidden"
      style={{
        height: '100dvh',
        boxSizing: 'border-box',
        backgroundColor: currentTheme.light,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <motion.div className="w-full h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full px-2">
            <RebuildBlockLogo className="w-full h-auto" scaleText={1} />
          </div>
        </div>

        <div className="max-w-sm mx-auto w-full pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))]">
          <h1 className="type-page-title mb-2" style={{ color: currentTheme.primary }}>
            Your journal is locked
          </h1>

          {error && <p className="text-red-500 text-center mb-4">{error}</p>}

          <button
            onClick={handleFaceIDUnlock}
            disabled={isAuthenticating}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 transition-all shadow-sm disabled:opacity-50"
            style={{
              backgroundColor: currentTheme.primary,
              color: '#2D2D35'
            }}
          >
            <Fingerprint size={18} strokeWidth={1.5} />
            {biometricsAvailable ? 'Unlock with Face ID' : 'Unlock'}
          </button>

          {!biometricsAvailable && (
            <p className="font-body text-xs text-center mt-3" style={{ color: currentTheme.textSecondary || '#73737D' }}>
              If Face ID is unavailable, iOS may ask for your device passcode.
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Passcode;